import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Wallet', 'Net Banking', 'Corporate'];

const TEMPLATES = [
  { id: 'pocket', label: 'Pocket Receipt', accent: '#f59e0b', format: [80, 297] },
  { id: 'banner', label: 'Bold Banner', accent: '#0ea5e9', format: [80, 297] },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: [80, 297] },
  { id: 'ride', label: 'Ride Summary A4', accent: '#16a34a', format: 'a4' },
  { id: 'invoice', label: 'A4 GST Invoice', accent: '#1d4ed8', format: 'a4' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const genBillNo = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(10000 + Math.random() * 90000);
  return `CAB-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newExtra = (name = '', amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  name,
  amount,
});

const CabBillGenerator = () => {
  const [template, setTemplate] = useState('pocket');

  const [operator, setOperator] = useState({
    name: 'City Cabs Pvt Ltd',
    address: '8 Outer Ring Road, Bengaluru 560037',
    phone: '+91 98765 43210',
    email: 'support@citycabs.in',
    gstin: '29ABCDE1234F1Z5',
    pan: 'ABCDE1234F',
  });

  const [meta, setMeta] = useState({
    billNo: genBillNo(),
    date: todayDMY(),
    paymentMode: 'UPI',
    bookingId: 'BK-' + Math.floor(100000 + Math.random() * 900000),
  });

  const [customer, setCustomer] = useState({
    name: 'Mr. Rohan Mehta',
    phone: '+91 90000 11122',
    email: '',
    gstin: '',
  });

  const [driver, setDriver] = useState({
    name: 'Suresh Kumar',
    licenseNo: 'KA0120190012345',
    phone: '+91 99999 88888',
  });

  const [vehicle, setVehicle] = useState({
    number: 'KA-01-AB-1234',
    model: 'Maruti Suzuki Dzire (Sedan)',
    type: 'Sedan',
  });

  const [trip, setTrip] = useState({
    pickup: 'Kempegowda Intl. Airport, T1',
    drop: '12, MG Road, Bengaluru 560001',
    pickupTime: '06:30',
    dropTime: '07:45',
    distanceKm: '38',
    durationMin: '75',
    waitMin: '8',
  });

  const [fare, setFare] = useState({
    baseFare: 60,
    perKm: 14,
    perMinute: 1.5,
    waitPerMinute: 1,
    nightCharge: 0,
    tollParking: 80,
    surgeMultiplier: 1,
    convenienceFee: 20,
    tipAmount: 0,
    discount: 0,
  });

  const [extras, setExtras] = useState([]);

  const [tax, setTax] = useState({
    cgst: 2.5,
    sgst: 2.5,
    roundOff: true,
  });

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#f59e0b';

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    const km = Number(trip.distanceKm) || 0;
    const dur = Number(trip.durationMin) || 0;
    const wait = Number(trip.waitMin) || 0;

    const base = Number(fare.baseFare) || 0;
    const distFare = km * (Number(fare.perKm) || 0);
    const timeFare = dur * (Number(fare.perMinute) || 0);
    const waitFare = wait * (Number(fare.waitPerMinute) || 0);
    const night = Number(fare.nightCharge) || 0;
    const surge = Number(fare.surgeMultiplier) || 1;
    const conv = Number(fare.convenienceFee) || 0;
    const toll = Number(fare.tollParking) || 0;
    const tip = Number(fare.tipAmount) || 0;
    const disc = Number(fare.discount) || 0;

    const rideSubtotal = (base + distFare + timeFare + waitFare + night) * surge;
    const extrasSum = extras.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    const subtotal = rideSubtotal + conv + extrasSum;
    const afterDisc = Math.max(0, subtotal - disc);

    const cgst = (afterDisc * (Number(tax.cgst) || 0)) / 100;
    const sgst = (afterDisc * (Number(tax.sgst) || 0)) / 100;
    const taxableTotal = afterDisc + cgst + sgst;
    const grand = taxableTotal + toll + tip;

    const grandRounded = Math.round(grand);
    return {
      base,
      distFare,
      timeFare,
      waitFare,
      night,
      surge,
      rideSubtotal,
      extrasSum,
      conv,
      toll,
      tip,
      disc,
      subtotal,
      afterDisc,
      cgst,
      sgst,
      grand: Math.round(grand * 100) / 100,
      grandRounded,
      roundOff: grandRounded - grand,
    };
  }, [trip, fare, tax, extras]);

  const finalAmount = tax.roundOff ? totals.grandRounded : totals.grand;

  const addExtra = () => setExtras((p) => [...p, newExtra()]);
  const updateExtra = (id, patch) =>
    setExtras((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeExtra = (id) => setExtras((p) => p.filter((x) => x.id !== id));

  const handleReset = () => {
    setOperator({ name: '', address: '', phone: '', email: '', gstin: '', pan: '' });
    setMeta({ billNo: genBillNo(), date: todayDMY(), paymentMode: 'Cash', bookingId: '' });
    setCustomer({ name: '', phone: '', email: '', gstin: '' });
    setDriver({ name: '', licenseNo: '', phone: '' });
    setVehicle({ number: '', model: '', type: 'Sedan' });
    setTrip({ pickup: '', drop: '', pickupTime: '', dropTime: '', distanceKm: '', durationMin: '', waitMin: '' });
    setFare({
      baseFare: 0, perKm: 0, perMinute: 0, waitPerMinute: 0, nightCharge: 0,
      tollParking: 0, surgeMultiplier: 1, convenienceFee: 0, tipAmount: 0, discount: 0,
    });
    setExtras([]);
    setTax({ cgst: 2.5, sgst: 2.5, roundOff: true });
  };

  // ---------- THERMAL HELPERS ----------
  const drawFareThermal = (doc, startY, w) => {
    let y = startY;
    const left = 5;
    const right = w - 5;
    const km = Number(trip.distanceKm) || 0;
    const dur = Number(trip.durationMin) || 0;
    const wait = Number(trip.waitMin) || 0;
    const surge = Number(fare.surgeMultiplier) || 1;

    const line = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 10 : 9);
      doc.setTextColor(0, 0, 0);
      doc.text(label, left, y);
      doc.text(value, right, y, { align: 'right' });
      y += bold ? 5 : 4.5;
    };

    line('Base Fare', formatINR(totals.base));
    if (km > 0) line(`Distance (${km} km @ ${fare.perKm})`, formatINR(totals.distFare));
    if (dur > 0 && Number(fare.perMinute) > 0)
      line(`Time (${dur} min @ ${fare.perMinute})`, formatINR(totals.timeFare));
    if (wait > 0 && Number(fare.waitPerMinute) > 0)
      line(`Wait (${wait} min)`, formatINR(totals.waitFare));
    if (Number(fare.nightCharge) > 0) line('Night Charge', formatINR(totals.night));
    if (surge !== 1) line(`Surge x${surge}`, '');

    line('Ride Subtotal', formatINR(totals.rideSubtotal), true);

    if (Number(fare.convenienceFee) > 0) line('Convenience Fee', formatINR(totals.conv));
    extras.forEach((x) => {
      if (Number(x.amount) > 0) line(x.name || 'Extra', formatINR(Number(x.amount)));
    });
    if (Number(fare.discount) > 0) line('Discount', `- ${formatINR(totals.disc)}`);
    if (totals.cgst > 0) line(`CGST ${tax.cgst}%`, formatINR(totals.cgst));
    if (totals.sgst > 0) line(`SGST ${tax.sgst}%`, formatINR(totals.sgst));
    if (Number(fare.tollParking) > 0) line('Toll / Parking', formatINR(totals.toll));
    if (Number(fare.tipAmount) > 0) line('Tip', formatINR(totals.tip));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      line('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    return y;
  };

  // 1. POCKET RECEIPT (thermal)
  const renderPocket = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 10;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, operator.name, [240, 240, 240]);
      y = 24;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...color);
    doc.text(operator.name || 'City Cabs', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    if (operator.address) {
      const lines = doc.splitTextToSize(operator.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (operator.phone) { doc.text(`Tel: ${operator.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    if (operator.gstin) { doc.text(`GSTIN: ${operator.gstin}`, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 1;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, w - 5, y);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('TRIP RECEIPT', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    if (meta.bookingId) { doc.text(`Booking: ${meta.bookingId}`, 5, y); y += 4; }
    y += 1;

    // Trip box
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('FROM', 5, y);
    y += 3.5;
    doc.setFont('helvetica', 'normal');
    const fromLines = doc.splitTextToSize(trip.pickup || '-', w - 10);
    fromLines.forEach((ln) => { doc.text(ln, 5, y); y += 3.5; });
    if (trip.pickupTime) { doc.text(`@ ${trip.pickupTime}`, 5, y); y += 3.5; }
    y += 1;
    doc.setFont('helvetica', 'bold');
    doc.text('TO', 5, y);
    y += 3.5;
    doc.setFont('helvetica', 'normal');
    const toLines = doc.splitTextToSize(trip.drop || '-', w - 10);
    toLines.forEach((ln) => { doc.text(ln, 5, y); y += 3.5; });
    if (trip.dropTime) { doc.text(`@ ${trip.dropTime}`, 5, y); y += 3.5; }
    y += 1;
    doc.text(`Distance: ${trip.distanceKm || 0} km   Duration: ${trip.durationMin || 0} min`, 5, y);
    y += 4;
    if (vehicle.number || vehicle.model) {
      doc.text(`Vehicle: ${vehicle.number || ''} ${vehicle.model ? '· ' + vehicle.model : ''}`, 5, y);
      y += 4;
    }
    if (driver.name) { doc.text(`Driver: ${driver.name}`, 5, y); y += 4; }
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    y = drawFareThermal(doc, y, w);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text('TOTAL', 5, y);
    doc.text(`Rs. ${formatINR(finalAmount)}`, w - 5, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('Thank you for riding with us!', w / 2, y, { align: 'center' });
    return doc;
  };

  // 2. BOLD BANNER (thermal)
  const renderBanner = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 0;
    const bannerH = logo ? 36 : 24;
    doc.setFillColor(...color);
    doc.rect(0, 0, w, bannerH, 'F');
    if (logo) drawPdfLogo(doc, logo, (w - 14) / 2, 3, 14, operator.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(operator.name || 'Cab', w / 2, logo ? 22 : 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('TRIP INVOICE', w / 2, logo ? 28 : 16, { align: 'center' });
    if (operator.gstin) doc.text(`GSTIN ${operator.gstin}`, w / 2, logo ? 32 : 20, { align: 'center' });

    y = bannerH + 4;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    if (operator.address) {
      const lines = doc.splitTextToSize(operator.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (operator.phone) { doc.text(`Tel: ${operator.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;

    doc.setFontSize(9);
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    if (customer.name) { doc.text(`Rider: ${customer.name}`, 5, y); y += 4; }
    if (vehicle.number) { doc.text(`Veh: ${vehicle.number}${vehicle.model ? ' · ' + vehicle.model : ''}`, 5, y); y += 4; }
    y += 1;

    // Trip
    doc.setFont('helvetica', 'bold');
    doc.text('Pickup:', 5, y);
    doc.setFont('helvetica', 'normal');
    const fl = doc.splitTextToSize(trip.pickup || '-', w - 22);
    fl.forEach((ln, i) => doc.text(ln, 22, y + i * 3.5));
    y += Math.max(4, fl.length * 3.5);
    doc.setFont('helvetica', 'bold');
    doc.text('Drop:', 5, y);
    doc.setFont('helvetica', 'normal');
    const dl = doc.splitTextToSize(trip.drop || '-', w - 22);
    dl.forEach((ln, i) => doc.text(ln, 22, y + i * 3.5));
    y += Math.max(4, dl.length * 3.5) + 1;
    doc.text(`${trip.distanceKm || 0} km · ${trip.durationMin || 0} min`, 5, y);
    y += 5;

    y = drawFareThermal(doc, y, w);
    y += 2;
    doc.setFillColor(...color);
    doc.rect(4, y, w - 8, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL', 6, y + 8);
    doc.text(`Rs. ${formatINR(finalAmount)}`, w - 6, y + 8, { align: 'right' });
    y += 16;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Thank you for riding with us!', w / 2, y, { align: 'center' });
    return doc;
  };

  // 3. MINIMAL MONO (thermal)
  const renderMinimal = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    let y = 12;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 14) / 2, 4, 14, operator.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(operator.name || 'Cab', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (operator.address) {
      const lines = doc.splitTextToSize(operator.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (operator.phone) { doc.text(operator.phone, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setFontSize(9);
    doc.text(meta.billNo, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    if (customer.name) { doc.text(`Rider: ${customer.name}`, 5, y); y += 4; }
    if (vehicle.number) { doc.text(`Vehicle: ${vehicle.number}`, 5, y); y += 4; }
    y += 1;
    doc.text(`From: ${trip.pickup || '-'}`, 5, y);
    y += 4;
    doc.text(`To: ${trip.drop || '-'}`, 5, y);
    y += 4;
    doc.text(`${trip.distanceKm || 0} km · ${trip.durationMin || 0} min`, 5, y);
    y += 5;

    y = drawFareThermal(doc, y, w);
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('TOTAL', 5, y);
    doc.text(`Rs. ${formatINR(finalAmount)}`, w - 5, y, { align: 'right' });
    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 4;
    doc.text('Thank you. Ride safe.', w / 2, y, { align: 'center' });
    return doc;
  };

  // 4. RIDE SUMMARY A4
  const renderRide = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    // Header band
    doc.setFillColor(...color);
    doc.rect(0, 0, w, 36, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 6, 24, operator.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(operator.name || 'City Cabs', logo ? left + 28 : left, 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('TRIP RECEIPT', logo ? left + 28 : left, 24);
    if (operator.gstin) doc.text(`GSTIN: ${operator.gstin}`, logo ? left + 28 : left, 30);
    doc.setFontSize(9);
    if (operator.address) {
      const al = doc.splitTextToSize(operator.address, 75);
      al.forEach((ln, i) => doc.text(ln, right, 12 + i * 4, { align: 'right' }));
    }
    if (operator.phone) doc.text(`Tel: ${operator.phone}`, right, 28, { align: 'right' });

    let y = 46;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Rider', left, y);
    doc.text('Trip Details', w / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(customer.name || '—', left, y);
    doc.text(`Bill No: ${meta.billNo}`, w / 2, y);
    y += 5;
    if (customer.phone) doc.text(customer.phone, left, y);
    doc.text(`Date: ${meta.date}`, w / 2, y);
    y += 5;
    if (customer.email) doc.text(customer.email, left, y);
    if (meta.bookingId) doc.text(`Booking: ${meta.bookingId}`, w / 2, y);
    y += 8;

    // Trip card
    doc.setFillColor(243, 244, 246);
    doc.rect(left, y, right - left, 36, 'F');
    doc.setDrawColor(...color);
    doc.setLineWidth(0.6);
    doc.line(left, y, left, y + 36);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...color);
    doc.text('PICKUP', left + 4, y + 6);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const pul = doc.splitTextToSize(trip.pickup || '-', (right - left) / 2 - 8);
    pul.forEach((ln, i) => doc.text(ln, left + 4, y + 11 + i * 4));
    if (trip.pickupTime) doc.text(`@ ${trip.pickupTime}`, left + 4, y + 11 + pul.length * 4);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...color);
    doc.text('DROP', w / 2 + 4, y + 6);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const dol = doc.splitTextToSize(trip.drop || '-', (right - left) / 2 - 8);
    dol.forEach((ln, i) => doc.text(ln, w / 2 + 4, y + 11 + i * 4));
    if (trip.dropTime) doc.text(`@ ${trip.dropTime}`, w / 2 + 4, y + 11 + dol.length * 4);

    y += 40;

    // Distance/Duration row
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Distance: ${trip.distanceKm || 0} km`, left + 4, y + 7);
    doc.text(`Duration: ${trip.durationMin || 0} min`, left + 70, y + 7);
    doc.text(`Wait: ${trip.waitMin || 0} min`, left + 130, y + 7);
    y += 14;

    // Vehicle + driver
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Vehicle', left, y);
    doc.text('Driver', w / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`${vehicle.number || '-'}${vehicle.model ? ' · ' + vehicle.model : ''}`, left, y);
    doc.text(driver.name || '-', w / 2, y);
    y += 5;
    if (driver.licenseNo) doc.text(`DL: ${driver.licenseNo}`, w / 2, y);
    if (vehicle.type) doc.text(`Type: ${vehicle.type}`, left, y);
    y += 8;

    // Fare table
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Fare Breakup', left + 3, y + 6);
    doc.text('Amount (Rs.)', right - 3, y + 6, { align: 'right' });
    y += 9;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.rect(left, y, right - left, 6.5);
      doc.text(label, left + 3, y + 4.5);
      doc.text(value, right - 3, y + 4.5, { align: 'right' });
      y += 6.5;
    };
    row('Base Fare', formatINR(totals.base));
    if (Number(trip.distanceKm) > 0) row(`Distance (${trip.distanceKm} km @ ${fare.perKm})`, formatINR(totals.distFare));
    if (Number(trip.durationMin) > 0 && Number(fare.perMinute) > 0)
      row(`Time (${trip.durationMin} min @ ${fare.perMinute})`, formatINR(totals.timeFare));
    if (Number(trip.waitMin) > 0 && Number(fare.waitPerMinute) > 0)
      row(`Wait Charge (${trip.waitMin} min)`, formatINR(totals.waitFare));
    if (Number(fare.nightCharge) > 0) row('Night Charge', formatINR(totals.night));
    if (Number(fare.surgeMultiplier) !== 1) row(`Surge x${fare.surgeMultiplier}`, '');
    row('Ride Subtotal', formatINR(totals.rideSubtotal), true);
    if (Number(fare.convenienceFee) > 0) row('Convenience Fee', formatINR(totals.conv));
    extras.forEach((x) => {
      if (Number(x.amount) > 0) row(x.name || 'Extra', formatINR(Number(x.amount)));
    });
    if (Number(fare.discount) > 0) row('Discount', `- ${formatINR(totals.disc)}`);
    if (totals.cgst > 0) row(`CGST (${tax.cgst}%)`, formatINR(totals.cgst));
    if (totals.sgst > 0) row(`SGST (${tax.sgst}%)`, formatINR(totals.sgst));
    if (Number(fare.tollParking) > 0) row('Toll / Parking', formatINR(totals.toll));
    if (Number(fare.tipAmount) > 0) row('Tip', formatINR(totals.tip));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);

    y += 2;
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('TOTAL FARE', left + 4, y + 8);
    doc.text(`Rs. ${formatINR(finalAmount)}`, right - 4, y + 8, { align: 'right' });
    y += 16;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Paid via ${meta.paymentMode}`, left, y);

    y += 24;
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Thank you for riding with us!', w / 2, 285, { align: 'center' });
    return doc;
  };

  // 5. A4 GST INVOICE
  const renderInvoice = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 32, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 4, 22, operator.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(operator.name || 'City Cabs', logo ? left + 26 : left, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('TAX INVOICE - TRANSPORT', logo ? left + 26 : left, 22);
    if (operator.gstin) doc.text(`GSTIN: ${operator.gstin}`, logo ? left + 26 : left, 28);
    doc.setFontSize(9);
    if (operator.address) {
      const al = doc.splitTextToSize(operator.address, 80);
      al.forEach((ln, i) => doc.text(ln, right, 12 + i * 4, { align: 'right' }));
    }
    if (operator.phone) doc.text(`Tel: ${operator.phone}`, right, 26, { align: 'right' });
    if (operator.pan) doc.text(`PAN: ${operator.pan}`, right, 30, { align: 'right' });

    let y = 42;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Billed To (Customer)', left, y);
    doc.text('Invoice Details', w / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(customer.name || '-', left, y);
    doc.text(`Invoice No: ${meta.billNo}`, w / 2, y);
    y += 5;
    if (customer.phone) doc.text(customer.phone, left, y);
    doc.text(`Date: ${meta.date}`, w / 2, y);
    y += 5;
    if (customer.email) doc.text(customer.email, left, y);
    if (meta.bookingId) doc.text(`Booking: ${meta.bookingId}`, w / 2, y);
    y += 5;
    if (customer.gstin) { doc.text(`Customer GSTIN: ${customer.gstin}`, left, y); y += 5; }
    y += 4;

    // Trip box
    doc.setFillColor(243, 244, 246);
    doc.rect(left, y, right - left, 22, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...color);
    doc.text('PICKUP', left + 3, y + 5);
    doc.text('DROP', w / 2 + 3, y + 5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const pul = doc.splitTextToSize(trip.pickup || '-', (right - left) / 2 - 8);
    pul.forEach((ln, i) => doc.text(ln, left + 3, y + 10 + i * 4));
    const dol = doc.splitTextToSize(trip.drop || '-', (right - left) / 2 - 8);
    dol.forEach((ln, i) => doc.text(ln, w / 2 + 3, y + 10 + i * 4));
    y += 24;

    doc.setFontSize(9);
    doc.text(`Distance: ${trip.distanceKm || 0} km   Duration: ${trip.durationMin || 0} min   Wait: ${trip.waitMin || 0} min`, left, y);
    y += 5;
    doc.text(`Vehicle: ${vehicle.number || '-'}${vehicle.model ? ' · ' + vehicle.model : ''}   Driver: ${driver.name || '-'}`, left, y);
    y += 8;

    // Fare table
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Description', left + 3, y + 6);
    doc.text('Amount (Rs.)', right - 3, y + 6, { align: 'right' });
    y += 9;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const row = (label, value) => {
      doc.rect(left, y, right - left, 6.5);
      doc.text(label, left + 3, y + 4.5);
      doc.text(value, right - 3, y + 4.5, { align: 'right' });
      y += 6.5;
    };
    row('Base Fare', formatINR(totals.base));
    if (Number(trip.distanceKm) > 0) row(`Distance Charges (${trip.distanceKm} km @ ${fare.perKm})`, formatINR(totals.distFare));
    if (Number(trip.durationMin) > 0 && Number(fare.perMinute) > 0)
      row(`Time Charges (${trip.durationMin} min)`, formatINR(totals.timeFare));
    if (Number(trip.waitMin) > 0 && Number(fare.waitPerMinute) > 0)
      row(`Wait Charges (${trip.waitMin} min)`, formatINR(totals.waitFare));
    if (Number(fare.nightCharge) > 0) row('Night Charges', formatINR(totals.night));
    if (Number(fare.surgeMultiplier) !== 1)
      row(`Surge Adjustment (x${fare.surgeMultiplier})`, formatINR(totals.rideSubtotal - (totals.base + totals.distFare + totals.timeFare + totals.waitFare + totals.night)));
    if (Number(fare.convenienceFee) > 0) row('Convenience Fee', formatINR(totals.conv));
    extras.forEach((x) => { if (Number(x.amount) > 0) row(x.name || 'Extra', formatINR(Number(x.amount))); });

    y += 4;
    const tx = right - 90;
    const trow = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, tx, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    trow('Subtotal', formatINR(totals.subtotal));
    if (Number(fare.discount) > 0) trow('Discount', `- ${formatINR(totals.disc)}`);
    if (totals.cgst > 0) trow(`CGST (${tax.cgst}%)`, formatINR(totals.cgst));
    if (totals.sgst > 0) trow(`SGST (${tax.sgst}%)`, formatINR(totals.sgst));
    if (Number(fare.tollParking) > 0) trow('Toll / Parking', formatINR(totals.toll));
    if (Number(fare.tipAmount) > 0) trow('Tip', formatINR(totals.tip));
    if (tax.roundOff && Math.abs(totals.roundOff) > 0.001)
      trow('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(tx, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    trow('TOTAL', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);

    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Payment Mode: ${meta.paymentMode}`, left, y);

    y += 24;
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Thank you for riding with us!', w / 2, 285, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    pocket: renderPocket,
    banner: renderBanner,
    minimal: renderMinimal,
    ride: renderRide,
    invoice: renderInvoice,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderPocket)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Cab_Bill_${meta.billNo || 'receipt'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Cab Bill / Taxi Receipt Generator"
        description="Free Cab Bill Generator India - create taxi & cab receipts with GST (CGST/SGST), distance & time fare, surge, toll, driver and vehicle details. 5 printable templates. Download PDF."
        keywords="cab bill generator, taxi receipt generator, ola uber bill format, cab invoice india, gst taxi bill, transport receipt, cab fare receipt pdf"
        canonicalPath="/cab-bill"
        calculatorType="CabBillGenerator"
        calculatorDescription="Generate Indian cab / taxi bills with itemised fare breakup, distance & time charges, surge, GST, driver & vehicle info, and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>Cab Bill Generator</h1>
        <p>Taxi / cab receipts with fare breakup, GST, surge, and 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Template */}
          <div className="input-group">
            <label>Bill Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: t.accent,
                        border: active ? '1px solid #fff' : 'none',
                      }}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="Cab Operator Logo" accent={accent} />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Operator Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Cab Operator Name</label>
              <input
                type="text"
                value={operator.name}
                onChange={(e) => setOperator({ ...operator, name: e.target.value })}
                placeholder="e.g. City Cabs Pvt Ltd"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input
                type="text"
                value={operator.phone}
                onChange={(e) => setOperator({ ...operator, phone: e.target.value })}
                placeholder="+91 ..."
              />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input
              type="text"
              value={operator.address}
              onChange={(e) => setOperator({ ...operator, address: e.target.value })}
              placeholder="Street, City, PIN"
            />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email</label>
              <input
                type="text"
                value={operator.email}
                onChange={(e) => setOperator({ ...operator, email: e.target.value })}
                placeholder="support@example.com"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN</label>
              <input
                type="text"
                value={operator.gstin}
                onChange={(e) => setOperator({ ...operator, gstin: e.target.value.toUpperCase() })}
                placeholder="29ABCDE1234F1Z5"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>PAN</label>
              <input
                type="text"
                value={operator.pan}
                onChange={(e) => setOperator({ ...operator, pan: e.target.value.toUpperCase() })}
                placeholder="ABCDE1234F"
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Bill Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Bill No</label>
              <input
                type="text"
                value={meta.billNo}
                onChange={(e) => setMeta({ ...meta, billNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input
                type="text"
                value={meta.date}
                onChange={(e) => setMeta({ ...meta, date: e.target.value })}
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Booking ID</label>
              <input
                type="text"
                value={meta.bookingId}
                onChange={(e) => setMeta({ ...meta, bookingId: e.target.value })}
                placeholder="BK-..."
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <select
                value={meta.paymentMode}
                onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}
              >
                {PAYMENT_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Customer (Rider)</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Name</label>
              <input
                type="text"
                value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input
                type="text"
                value={customer.phone}
                onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
              />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email (optional)</label>
              <input
                type="text"
                value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Customer GSTIN (optional)</label>
              <input
                type="text"
                value={customer.gstin}
                onChange={(e) => setCustomer({ ...customer, gstin: e.target.value.toUpperCase() })}
                placeholder="For corporate bookings"
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Vehicle & Driver</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Vehicle Number</label>
              <input
                type="text"
                value={vehicle.number}
                onChange={(e) => setVehicle({ ...vehicle, number: e.target.value.toUpperCase() })}
                placeholder="KA-01-AB-1234"
              />
            </div>
            <div className="input-group" style={{ flex: 2 }}>
              <label>Vehicle Model</label>
              <input
                type="text"
                value={vehicle.model}
                onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })}
                placeholder="e.g. Maruti Dzire"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Type</label>
              <select
                value={vehicle.type}
                onChange={(e) => setVehicle({ ...vehicle, type: e.target.value })}
              >
                <option>Mini</option>
                <option>Sedan</option>
                <option>SUV</option>
                <option>Premium</option>
                <option>Auto</option>
                <option>Bike</option>
              </select>
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Driver Name</label>
              <input
                type="text"
                value={driver.name}
                onChange={(e) => setDriver({ ...driver, name: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Driver Licence No</label>
              <input
                type="text"
                value={driver.licenseNo}
                onChange={(e) => setDriver({ ...driver, licenseNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Driver Phone</label>
              <input
                type="text"
                value={driver.phone}
                onChange={(e) => setDriver({ ...driver, phone: e.target.value })}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Trip Details</h3>
          <div className="input-group">
            <label>Pickup Location</label>
            <input
              type="text"
              value={trip.pickup}
              onChange={(e) => setTrip({ ...trip, pickup: e.target.value })}
              placeholder="From"
            />
          </div>
          <div className="input-group">
            <label>Drop Location</label>
            <input
              type="text"
              value={trip.drop}
              onChange={(e) => setTrip({ ...trip, drop: e.target.value })}
              placeholder="To"
            />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Pickup Time</label>
              <input
                type="text"
                value={trip.pickupTime}
                onChange={(e) => setTrip({ ...trip, pickupTime: e.target.value })}
                placeholder="HH:MM"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Drop Time</label>
              <input
                type="text"
                value={trip.dropTime}
                onChange={(e) => setTrip({ ...trip, dropTime: e.target.value })}
                placeholder="HH:MM"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Distance (km)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={trip.distanceKm}
                onChange={(e) => setTrip({ ...trip, distanceKm: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Duration (min)</label>
              <input
                type="number"
                min="0"
                value={trip.durationMin}
                onChange={(e) => setTrip({ ...trip, durationMin: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Wait (min)</label>
              <input
                type="number"
                min="0"
                value={trip.waitMin}
                onChange={(e) => setTrip({ ...trip, waitMin: e.target.value })}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Fare Configuration</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Base Fare (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.baseFare}
                onChange={(e) => setFare({ ...fare, baseFare: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Rate / km (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.perKm}
                onChange={(e) => setFare({ ...fare, perKm: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Rate / min (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.perMinute}
                onChange={(e) => setFare({ ...fare, perMinute: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Wait / min (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.waitPerMinute}
                onChange={(e) => setFare({ ...fare, waitPerMinute: e.target.value })}
              />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Night Charge (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.nightCharge}
                onChange={(e) => setFare({ ...fare, nightCharge: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Surge Multiplier</label>
              <input
                type="number"
                min="1"
                step="0.1"
                value={fare.surgeMultiplier}
                onChange={(e) => setFare({ ...fare, surgeMultiplier: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Convenience Fee (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.convenienceFee}
                onChange={(e) => setFare({ ...fare, convenienceFee: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Discount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.discount}
                onChange={(e) => setFare({ ...fare, discount: e.target.value })}
              />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Toll / Parking (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.tollParking}
                onChange={(e) => setFare({ ...fare, tollParking: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Tip (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fare.tipAmount}
                onChange={(e) => setFare({ ...fare, tipAmount: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>CGST %</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={tax.cgst}
                onChange={(e) => setTax({ ...tax, cgst: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>SGST %</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={tax.sgst}
                onChange={(e) => setTax({ ...tax, sgst: e.target.value })}
              />
            </div>
          </div>
          <div className="input-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={tax.roundOff}
                onChange={(e) => setTax({ ...tax, roundOff: e.target.checked })}
              />
              Round off total to nearest rupee
            </label>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Standard cab GST in India is 5% (CGST 2.5% + SGST 2.5%). Toll & tip are added after tax.
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 8 }}>Extra Charges (optional)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {extras.map((x) => (
              <div key={x.id} className="flex-row" style={{ alignItems: 'flex-end' }}>
                <div className="input-group" style={{ flex: 2, marginBottom: 0 }}>
                  <label>Label</label>
                  <input
                    type="text"
                    value={x.name}
                    onChange={(e) => updateExtra(x.id, { name: e.target.value })}
                    placeholder="e.g. Airport Entry Fee"
                  />
                </div>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={x.amount}
                    onChange={(e) => updateExtra(x.id, { amount: e.target.value })}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeExtra(x.id)}
                  className="btn btn-outline"
                  style={{ padding: '8px 10px', marginBottom: 0 }}
                  title="Remove"
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addExtra}
            className="btn btn-outline"
            style={{ marginTop: 10 }}
          >
            <FaPlus /> Add Extra Charge
          </button>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button type="button" onClick={handleDownloadPDF} className="btn btn-primary">
              <FaDownload /> Download PDF
            </button>
            <button type="button" onClick={handlePrint} className="btn btn-outline">
              <FaPrint /> Print
            </button>
            <button type="button" onClick={handleReset} className="btn btn-outline">
              <FaRedo /> Reset
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
            Live preview · Template: <strong>{TEMPLATES.find((t) => t.id === template)?.label}</strong>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <BillPreview
              template={template}
              accent={accent}
              operator={operator}
              customer={customer}
              driver={driver}
              vehicle={vehicle}
              trip={trip}
              fare={fare}
              extras={extras}
              meta={meta}
              tax={tax}
              totals={totals}
              finalAmount={finalAmount}
              logo={logo}
            />
          </div>
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: 8,
              fontSize: 13,
              color: '#92400e',
            }}
          >
            <strong>Total Fare:</strong> ₹ {formatINR(finalAmount)} · {trip.distanceKm || 0} km
            {totals.cgst + totals.sgst > 0 && <> · GST: ₹ {formatINR(totals.cgst + totals.sgst)}</>}
            {totals.toll > 0 && <> · Toll: ₹ {formatINR(totals.toll)}</>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- LIVE PREVIEW ----------
const previewBase = {
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#0f172a',
};

const LogoImg = ({ logo, style }) =>
  logo ? <img src={logo} alt="logo" style={{ objectFit: 'contain', ...style }} /> : null;

const BillPreview = ({ template, accent, operator, customer, driver, vehicle, trip, fare, extras, meta, tax, totals, finalAmount, logo }) => {
  const f = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const Row = ({ label, value, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: bold ? 700 : 400 }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  const FareLines = ({ compact }) => (
    <div style={{ fontSize: compact ? 11 : 12 }}>
      <Row label="Base Fare" value={f(totals.base)} />
      {Number(trip.distanceKm) > 0 && (
        <Row label={`Distance (${trip.distanceKm} km @ ${fare.perKm})`} value={f(totals.distFare)} />
      )}
      {Number(trip.durationMin) > 0 && Number(fare.perMinute) > 0 && (
        <Row label={`Time (${trip.durationMin} min @ ${fare.perMinute})`} value={f(totals.timeFare)} />
      )}
      {Number(trip.waitMin) > 0 && Number(fare.waitPerMinute) > 0 && (
        <Row label={`Wait (${trip.waitMin} min)`} value={f(totals.waitFare)} />
      )}
      {Number(fare.nightCharge) > 0 && <Row label="Night Charge" value={f(totals.night)} />}
      {Number(fare.surgeMultiplier) !== 1 && <Row label={`Surge x${fare.surgeMultiplier}`} value="" />}
      <Row label="Ride Subtotal" value={f(totals.rideSubtotal)} bold />
      {Number(fare.convenienceFee) > 0 && <Row label="Convenience Fee" value={f(totals.conv)} />}
      {extras.map((x) =>
        Number(x.amount) > 0 ? <Row key={x.id} label={x.name || 'Extra'} value={f(Number(x.amount))} /> : null
      )}
      {Number(fare.discount) > 0 && <Row label="Discount" value={`- ${f(totals.disc)}`} />}
      {totals.cgst > 0 && <Row label={`CGST (${tax.cgst}%)`} value={f(totals.cgst)} />}
      {totals.sgst > 0 && <Row label={`SGST (${tax.sgst}%)`} value={f(totals.sgst)} />}
      {Number(fare.tollParking) > 0 && <Row label="Toll / Parking" value={f(totals.toll)} />}
      {Number(fare.tipAmount) > 0 && <Row label="Tip" value={f(totals.tip)} />}
      {tax.roundOff && Math.abs(totals.roundOff) > 0.001 && (
        <Row
          label="Round Off"
          value={totals.roundOff >= 0 ? `+ ${f(totals.roundOff)}` : `- ${f(Math.abs(totals.roundOff))}`}
        />
      )}
      <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
        <Row
          label={<span style={{ color: accent, fontWeight: 800 }}>TOTAL FARE</span>}
          value={<span style={{ color: accent, fontWeight: 800 }}>₹ {f(finalAmount)}</span>}
        />
      </div>
    </div>
  );

  // 1. POCKET RECEIPT
  if (template === 'pocket') {
    return (
      <div style={{ ...previewBase, padding: 14, maxWidth: 320, margin: '12px auto', border: '1px dashed #cbd5e1' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <LogoImg logo={logo} style={{ height: 46 }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 16, color: accent }}>
          {operator.name || 'City Cabs'}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
          {operator.address}
          {operator.phone && <><br />Tel: {operator.phone}</>}
          {operator.gstin && <><br />GSTIN: {operator.gstin}</>}
        </div>
        <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
        <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 700 }}>TRIP RECEIPT</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
          <span>Bill: <strong>{meta.billNo}</strong></span>
          <span>{meta.date}</span>
        </div>
        {meta.bookingId && (
          <div style={{ fontSize: 11 }}>Booking: <strong>{meta.bookingId}</strong></div>
        )}
        <div style={{ marginTop: 8, fontSize: 11 }}>
          <div style={{ fontWeight: 700, color: accent }}>FROM</div>
          <div>{trip.pickup || '—'}{trip.pickupTime && ` · ${trip.pickupTime}`}</div>
          <div style={{ fontWeight: 700, color: accent, marginTop: 6 }}>TO</div>
          <div>{trip.drop || '—'}{trip.dropTime && ` · ${trip.dropTime}`}</div>
          <div style={{ marginTop: 6, color: '#64748b' }}>
            {trip.distanceKm || 0} km · {trip.durationMin || 0} min
          </div>
          {(vehicle.number || vehicle.model) && (
            <div style={{ marginTop: 4 }}>
              <strong>{vehicle.number}</strong>{vehicle.model && ` · ${vehicle.model}`}
            </div>
          )}
          {driver.name && <div>Driver: <strong>{driver.name}</strong></div>}
        </div>
        <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
        <FareLines compact />
        <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
        <div style={{ marginTop: 6, textAlign: 'center', fontStyle: 'italic', color: '#94a3b8', fontSize: 11 }}>
          Thank you for riding with us!
        </div>
      </div>
    );
  }

  // 2. BANNER
  if (template === 'banner') {
    return (
      <div style={{ ...previewBase, maxWidth: 320, margin: '12px auto', border: '1px solid #e2e8f0' }}>
        <div style={{ background: accent, color: '#fff', padding: 14, textAlign: 'center' }}>
          {logo && (
            <div style={{ background: '#fff', display: 'inline-block', padding: 3, borderRadius: 6, marginBottom: 6 }}>
              <LogoImg logo={logo} style={{ height: 40 }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 17 }}>{operator.name || 'City Cabs'}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            TRIP INVOICE{operator.gstin && ` · GSTIN ${operator.gstin}`}
          </div>
        </div>
        <div style={{ padding: 14, fontSize: 11, color: '#475569', textAlign: 'center' }}>
          {operator.address}{operator.phone ? ` · Tel: ${operator.phone}` : ''}
        </div>
        <div style={{ padding: '0 14px', display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
          <span>Bill: <strong>{meta.billNo}</strong></span>
          <span>{meta.date}</span>
        </div>
        <div style={{ padding: '6px 14px', fontSize: 11 }}>
          {customer.name && <div>Rider: <strong>{customer.name}</strong></div>}
          {vehicle.number && <div>Vehicle: <strong>{vehicle.number}</strong>{vehicle.model && ` · ${vehicle.model}`}</div>}
          <div style={{ marginTop: 4 }}><strong>Pickup:</strong> {trip.pickup || '—'}</div>
          <div><strong>Drop:</strong> {trip.drop || '—'}</div>
          <div style={{ color: '#64748b', marginTop: 2 }}>{trip.distanceKm || 0} km · {trip.durationMin || 0} min</div>
        </div>
        <div style={{ padding: '0 14px' }}>
          <FareLines compact />
        </div>
        <div style={{ margin: '10px 14px 0', background: accent, color: '#fff', padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          <span>TOTAL</span>
          <span>₹ {f(finalAmount)}</span>
        </div>
        <div style={{ padding: 12, textAlign: 'center', fontSize: 11, color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
      </div>
    );
  }

  // 3. MINIMAL
  if (template === 'minimal') {
    return (
      <div style={{ ...previewBase, padding: 16, maxWidth: 320, margin: '12px auto', border: '1px solid #cbd5e1' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <LogoImg logo={logo} style={{ height: 40 }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15 }}>{operator.name || 'Cab'}</div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {operator.address}{operator.phone ? ` · ${operator.phone}` : ''}
        </div>
        <div style={{ borderTop: '2px solid #0f172a', margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
          <span>{meta.billNo}</span>
          <span>{meta.date}</span>
        </div>
        <div style={{ fontSize: 11, marginTop: 4 }}>
          {customer.name && <div>Rider: {customer.name}</div>}
          {vehicle.number && <div>Vehicle: {vehicle.number}</div>}
          <div style={{ marginTop: 4 }}>From: {trip.pickup || '—'}</div>
          <div>To: {trip.drop || '—'}</div>
          <div style={{ color: '#64748b' }}>{trip.distanceKm || 0} km · {trip.durationMin || 0} min</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <FareLines compact />
        </div>
        <div style={{ marginTop: 6, fontSize: 10, fontStyle: 'italic', color: '#94a3b8', textAlign: 'center' }}>
          Paid via {meta.paymentMode} · Ride safe.
        </div>
      </div>
    );
  }

  // 4. RIDE SUMMARY A4
  if (template === 'ride') {
    return (
      <div style={{ ...previewBase }}>
        <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
              <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>{operator.name || 'City Cabs'}</div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>TRIP RECEIPT</div>
            {operator.gstin && <div style={{ fontSize: 11 }}>GSTIN: {operator.gstin}</div>}
          </div>
          <div style={{ textAlign: 'right', fontSize: 11 }}>
            {operator.address && <div>{operator.address}</div>}
            {operator.phone && <div>Tel: {operator.phone}</div>}
          </div>
        </div>

        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div>
            <div style={{ fontWeight: 700 }}>Rider</div>
            <div>{customer.name || '—'}</div>
            {customer.phone && <div style={{ fontSize: 11, color: '#64748b' }}>{customer.phone}</div>}
            {customer.email && <div style={{ fontSize: 11, color: '#64748b' }}>{customer.email}</div>}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>Trip Details</div>
            <div>Bill No: {meta.billNo}</div>
            <div>Date: {meta.date}</div>
            {meta.bookingId && <div>Booking: {meta.bookingId}</div>}
          </div>
        </div>

        <div style={{ margin: '0 16px', background: '#f3f4f6', borderLeft: `4px solid ${accent}`, padding: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: accent, fontSize: 10, letterSpacing: 1 }}>PICKUP</div>
            <div style={{ fontSize: 12 }}>{trip.pickup || '—'}</div>
            {trip.pickupTime && <div style={{ fontSize: 11, color: '#64748b' }}>@ {trip.pickupTime}</div>}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: accent, fontSize: 10, letterSpacing: 1 }}>DROP</div>
            <div style={{ fontSize: 12 }}>{trip.drop || '—'}</div>
            {trip.dropTime && <div style={{ fontSize: 11, color: '#64748b' }}>@ {trip.dropTime}</div>}
          </div>
        </div>

        <div style={{ margin: '12px 16px 0', background: accent, color: '#fff', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700 }}>
          <span>Distance: {trip.distanceKm || 0} km</span>
          <span>Duration: {trip.durationMin || 0} min</span>
          <span>Wait: {trip.waitMin || 0} min</span>
        </div>

        <div style={{ padding: '12px 16px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div>
            <div style={{ fontWeight: 700 }}>Vehicle</div>
            <div>{vehicle.number || '—'}{vehicle.model && ` · ${vehicle.model}`}</div>
            {vehicle.type && <div style={{ fontSize: 11, color: '#64748b' }}>Type: {vehicle.type}</div>}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>Driver</div>
            <div>{driver.name || '—'}</div>
            {driver.licenseNo && <div style={{ fontSize: 11, color: '#64748b' }}>DL: {driver.licenseNo}</div>}
          </div>
        </div>

        <div style={{ padding: '12px 16px 16px' }}>
          <div style={{ background: accent, color: '#fff', padding: '8px 10px', fontWeight: 700, fontSize: 12 }}>
            Fare Breakup
          </div>
          <div style={{ border: '1px solid #e2e8f0', borderTop: 0, padding: 10 }}>
            <FareLines />
          </div>
          <div style={{ marginTop: 10, fontSize: 12 }}>
            Paid via <strong>{meta.paymentMode}</strong>
          </div>
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
              Authorised Signatory
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 5. A4 GST INVOICE
  return (
    <div style={{ ...previewBase }}>
      <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        {logo && (
          <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
            <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{operator.name || 'City Cabs'}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>TAX INVOICE - TRANSPORT</div>
          {operator.gstin && <div style={{ fontSize: 11 }}>GSTIN: {operator.gstin}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: 11 }}>
          {operator.address && <div>{operator.address}</div>}
          {operator.phone && <div>Tel: {operator.phone}</div>}
          {operator.pan && <div>PAN: {operator.pan}</div>}
        </div>
      </div>

      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Billed To (Customer)</div>
          <div>{customer.name || '—'}</div>
          {customer.phone && <div style={{ fontSize: 11, color: '#64748b' }}>{customer.phone}</div>}
          {customer.email && <div style={{ fontSize: 11, color: '#64748b' }}>{customer.email}</div>}
          {customer.gstin && <div style={{ fontSize: 11 }}>GSTIN: {customer.gstin}</div>}
        </div>
        <div>
          <div style={{ fontWeight: 700 }}>Invoice Details</div>
          <div>Invoice No: {meta.billNo}</div>
          <div>Date: {meta.date}</div>
          {meta.bookingId && <div>Booking: {meta.bookingId}</div>}
        </div>
      </div>

      <div style={{ margin: '0 16px', background: '#f3f4f6', padding: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
        <div>
          <div style={{ fontWeight: 700, color: accent, fontSize: 10, letterSpacing: 1 }}>PICKUP</div>
          <div>{trip.pickup || '—'}</div>
        </div>
        <div>
          <div style={{ fontWeight: 700, color: accent, fontSize: 10, letterSpacing: 1 }}>DROP</div>
          <div>{trip.drop || '—'}</div>
        </div>
      </div>

      <div style={{ padding: '8px 16px 0', fontSize: 11, color: '#475569' }}>
        Distance: {trip.distanceKm || 0} km · Duration: {trip.durationMin || 0} min · Wait: {trip.waitMin || 0} min
        <br />
        Vehicle: {vehicle.number || '—'}{vehicle.model && ` · ${vehicle.model}`} · Driver: {driver.name || '—'}
      </div>

      <div style={{ padding: '12px 16px 16px' }}>
        <FareLines />
        <div style={{ marginTop: 10, fontSize: 12 }}>
          Payment Mode: <strong>{meta.paymentMode}</strong>
        </div>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
            Authorised Signatory
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, fontStyle: 'italic', color: accent, textAlign: 'center' }}>
          Thank you for riding with us!
        </div>
      </div>
    </div>
  );
};

export default CabBillGenerator;
